import { useCallback, useEffect, useRef, useState } from "react";
import { client } from "../Clients";
import type { MetricSnapshot, APAADMINAPIControllersMetricPoint } from "../API/Models";

export interface StatHistoryPoint {
	date: string;
	snapshot: MetricSnapshot;
}

const normalizePoint = (point?: APAADMINAPIControllersMetricPoint | null): StatHistoryPoint | null => {
	if (!point?.snapshot) {
		return null;
	}
	return {
		date: point.date ?? "",
		snapshot: point.snapshot
	};
};

export const useStatsData = () => {
	const [stats, setStats] = useState<MetricSnapshot | null>(null);
	const [history, setHistory] = useState<StatHistoryPoint[]>([]);
	const [loading, setLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const isMountedRef = useRef(true);

	const fetchStats = useCallback(
		async (manual = false) => {
			if (manual) {
				setIsRefreshing(true);
			} else {
				setLoading(true);
			}
			setError(null);
			try {
				const response = await client.metrics.apiMetricsGet(7);
				if (!isMountedRef.current) return;

				const historyPoints =
					response.data?.history
						?.map((point) => normalizePoint(point))
						.filter((point): point is StatHistoryPoint => Boolean(point)) ?? [];

				const currentPoint = normalizePoint(response.data?.current);

				setStats(currentPoint?.snapshot ?? null);
				setHistory([
					...historyPoints,
					...(currentPoint ? [currentPoint] : [])
				]);
			} catch (caughtError) {
				if (!isMountedRef.current) return;
				console.error(caughtError);
				setError("Unable to load stats. Please try again.");
			} finally {
				if (!isMountedRef.current) return;
				if (manual) {
					setIsRefreshing(false);
				} else {
					setLoading(false);
				}
			}
		},
		[]
	);

	useEffect(() => {
		isMountedRef.current = true;
		fetchStats();
		return () => {
			isMountedRef.current = false;
		};
	}, [fetchStats]);

	return {
		stats,
		history,
		loading,
		error,
		isRefreshing,
		refresh: () => fetchStats(true)
	};
};

export default useStatsData;

